// Test de Denver II interactivo (028) — cuadrícula de tamizaje del
// desarrollo psicomotor, 4 franjas (Personal-Social, Motor Fino-Adaptativo,
// Lenguaje, Motor Grueso), ~106 ítems posicionados por edad sobre un eje
// compartido con quiebre de escala (0-24 meses vs. 2-6 años, igual que el
// original impreso — ver `mesesAPosicionEje` en `@logiacare/hce-pdf`).
// Estructuralmente más parecido al odontograma (033) que a una curva de
// percentil: geometría fija del formulario impreso + una capa de
// interacción, no datos calculados — mismo criterio ya usado para decidir
// que esto es HTML/CSS bespoke, no el motor de `recharts` de Fase 1.
//
// Carriles (2026-09-16, pedido de Sue tras ver las pestañas apiñadas): el
// original impreso resuelve la densidad de ítems dibujando cada franja como
// una ESCALERA diagonal — cada ítem siguiente se desplaza en edad Y en
// posición vertical dentro de su franja, precisamente para no superponerse
// con el anterior. Acá se reproduce ese mismo mecanismo con
// `asignarCarriles`: primer-hueco-libre (interval scheduling) sobre la
// posición en píxeles real de cada ítem, no una fila plana — no es solo
// estética, es más fiel a cómo el formulario oficial resuelve este mismo
// problema de densidad.
import { type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CATALOGO_ITEMS_DENVER, FRANJAS_DENVER, mesesAPosicionEje, type FranjaDenver, type ItemDenver, type ResultadoItemDenver } from '@logiacare/hce-pdf';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const LABEL_W = 156;
const CONTENT_W = 2600;
export const CHIP_W = 140;
const CHIP_H = 44;
const GAP_CARRIL = 10;
const GAP_HORIZONTAL = 12;

const RESULTADOS: readonly ResultadoItemDenver[] = ['aprobado', 'fallado', 'rehuso', 'sin-oportunidad'];

/** Colores 100% de la paleta `hce-*` ya establecida — sage (éxito) y clay (alerta) ya se usan con ese sentido en el resto de la app; pine y muted completan los 4 estados sin agregar ningún color nuevo al sistema. */
const ESTILO_POR_RESULTADO: Record<ResultadoItemDenver, { variable: string; texto: string }> = {
  aprobado: { variable: '--color-hce-sage', texto: '#ffffff' },
  fallado: { variable: '--color-hce-clay', texto: '#ffffff' },
  rehuso: { variable: '--color-hce-pine', texto: '#ffffff' },
  'sin-oportunidad': { variable: '--color-hce-muted', texto: '#ffffff' },
};

function centroPx(item: ItemDenver): number {
  return mesesAPosicionEje(item.edadMeses) * CONTENT_W;
}

/** Borde izquierdo YA acotado a los límites del lienzo — la única fuente de verdad de posición horizontal, usada tanto para asignar carriles como para dibujar el chip. Antes `asignarCarriles` acotaba con el centro sin recortar y `ItemChip` sí recortaba: un ítem cerca del extremo derecho del eje (72 meses) podía "saltar" hasta 66px hacia la izquierda al dibujarse y pisar al ítem anterior de su mismo carril, aunque el cálculo de carriles lo hubiera dado por libre de superposición. Exportada para test, junto con `CHIP_W`, por la misma razón. */
export function posicionIzquierda(item: ItemDenver): number {
  return Math.min(Math.max(centroPx(item) - CHIP_W / 2, 0), CONTENT_W - CHIP_W);
}

/** Primer-hueco-libre: recorre los ítems en orden de edad y asigna cada uno al primer carril cuyo último ítem ya no se superpone (con `GAP_HORIZONTAL` de margen), sobre la posición YA acotada de cada chip. Abre un carril nuevo si ninguno tiene lugar. Exportada para test — es la única lógica no trivial de este componente. */
export function asignarCarriles(items: readonly ItemDenver[]): { carrilPorId: Map<string, number>; totalCarriles: number } {
  const bordeDerechoPorCarril: number[] = [];
  const carrilPorId = new Map<string, number>();
  for (const item of items) {
    const izquierda = posicionIzquierda(item);
    const derecha = izquierda + CHIP_W;
    let carril = bordeDerechoPorCarril.findIndex((borde) => izquierda >= borde + GAP_HORIZONTAL);
    if (carril === -1) {
      carril = bordeDerechoPorCarril.length;
      bordeDerechoPorCarril.push(derecha);
    } else {
      bordeDerechoPorCarril[carril] = derecha;
    }
    carrilPorId.set(item.id, carril);
  }
  return { carrilPorId, totalCarriles: bordeDerechoPorCarril.length };
}

function ItemChip({
  item,
  top,
  resultado,
  onChange,
}: {
  readonly item: ItemDenver;
  readonly top: number;
  readonly resultado: ResultadoItemDenver | undefined;
  readonly onChange: (resultado: ResultadoItemDenver | undefined) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  const estilo = resultado ? ESTILO_POR_RESULTADO[resultado] : undefined;
  const izquierda = posicionIzquierda(item);

  return (
    <div className="absolute" style={{ left: izquierda, top, width: CHIP_W, height: CHIP_H }}>
      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="group relative flex h-full w-full items-center justify-center rounded-md border px-2 text-center text-[11px] leading-[1.2] font-medium shadow-sm transition-all duration-150 ease-out outline-none hover:-translate-y-px hover:shadow-md focus-visible:ring-2 focus-visible:ring-hce-pine focus-visible:ring-offset-1"
            style={{
              background: estilo ? `var(${estilo.variable})` : 'var(--color-hce-paper)',
              color: estilo ? estilo.texto : 'var(--color-hce-ink)',
              borderColor: estilo ? `var(${estilo.variable})` : 'var(--border)',
            }}
          >
            {item.reporte && (
              <span
                className="absolute -top-1.5 -right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border text-[8px] leading-none font-semibold"
                style={{
                  background: estilo ? 'var(--color-hce-surface)' : 'var(--color-hce-surface)',
                  color: 'var(--color-hce-muted)',
                  borderColor: 'var(--border)',
                }}
                title={t('formularios.f028.reportadoPorCuidador')}
              >
                R
              </span>
            )}
            <span className="line-clamp-2">{item.texto}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="flex w-56 flex-col gap-2">
          <p className="text-sm font-medium text-hce-ink">{item.texto}</p>
          {item.nota !== undefined && <p className="text-xs text-hce-muted">{t('formularios.f028.notaRef', { numero: item.nota })}</p>}
          <div className="grid grid-cols-2 gap-1.5">
            {RESULTADOS.map((r) => (
              <button
                key={r}
                type="button"
                className="rounded-md border border-border px-2 py-1 text-xs transition-colors"
                style={resultado === r ? { background: `var(${ESTILO_POR_RESULTADO[r].variable})`, color: ESTILO_POR_RESULTADO[r].texto, borderColor: `var(${ESTILO_POR_RESULTADO[r].variable})` } : undefined}
                onClick={() => {
                  onChange(resultado === r ? undefined : r);
                  setAbierto(false);
                }}
              >
                {t(`formularios.f028.resultados.${r}`)}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FilaFranja({
  franja,
  etiqueta,
  estado,
  onChangeItem,
}: {
  readonly franja: FranjaDenver;
  readonly etiqueta: string;
  readonly estado: Readonly<Record<string, ResultadoItemDenver>>;
  readonly onChangeItem: (itemId: string, resultado: ResultadoItemDenver | undefined) => void;
}): JSX.Element {
  const items = useMemo(() => CATALOGO_ITEMS_DENVER.filter((i) => i.franja === franja), [franja]);
  const { carrilPorId, totalCarriles } = useMemo(() => asignarCarriles(items), [items]);
  const alto = totalCarriles * (CHIP_H + GAP_CARRIL) - GAP_CARRIL;

  return (
    <div className="grid border-t border-border" style={{ gridTemplateColumns: `${LABEL_W}px 1fr` }}>
      <div className="sticky left-0 z-10 flex items-start bg-hce-surface py-4 pr-2 pl-3 text-sm font-medium text-hce-ink">{etiqueta}</div>
      <div className="relative py-4" style={{ height: alto + 32, minWidth: CONTENT_W }}>
        {items.map((item) => (
          <ItemChip key={item.id} item={item} top={(carrilPorId.get(item.id) ?? 0) * (CHIP_H + GAP_CARRIL)} resultado={estado[item.id]} onChange={(r) => onChangeItem(item.id, r)} />
        ))}
      </div>
    </div>
  );
}

const TICKS_EJE = [
  { meses: 2, etiqueta: '2m' },
  { meses: 4, etiqueta: '4m' },
  { meses: 6, etiqueta: '6m' },
  { meses: 9, etiqueta: '9m' },
  { meses: 12, etiqueta: '12m' },
  { meses: 18, etiqueta: '18m' },
  { meses: 24, etiqueta: '2a' },
  { meses: 36, etiqueta: '3a' },
  { meses: 48, etiqueta: '4a' },
  { meses: 60, etiqueta: '5a' },
  { meses: 72, etiqueta: '6a' },
];

function EjeEdad(): JSX.Element {
  return (
    <div className="grid" style={{ gridTemplateColumns: `${LABEL_W}px 1fr` }}>
      <div className="sticky left-0 z-10 bg-hce-surface" />
      <div className="relative h-6 border-b border-border" style={{ minWidth: CONTENT_W }}>
        {TICKS_EJE.map((t) => (
          <span key={t.meses} className="absolute -translate-x-1/2 text-[11px] font-medium text-hce-muted" style={{ left: mesesAPosicionEje(t.meses) * CONTENT_W }}>
            {t.etiqueta}
          </span>
        ))}
      </div>
    </div>
  );
}

export interface DenverIIVisualProps {
  readonly estado: Readonly<Record<string, ResultadoItemDenver>>;
  readonly onChange: (estado: Readonly<Record<string, ResultadoItemDenver>>) => void;
  /** Edad del paciente al momento de la evaluación (meses) — dibuja la línea de "punto de prueba" vertical, si se conoce. */
  readonly edadEvaluacionMeses?: number;
}

export function DenverIIVisual({ estado, onChange, edadEvaluacionMeses }: DenverIIVisualProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarItem(itemId: string, resultado: ResultadoItemDenver | undefined): void {
    if (resultado === undefined) {
      const { [itemId]: _omitido, ...resto } = estado;
      onChange(resto);
      return;
    }
    onChange({ ...estado, [itemId]: resultado });
  }

  const lineaEdadIzquierda = edadEvaluacionMeses !== undefined ? LABEL_W + Math.min(Math.max(mesesAPosicionEje(edadEvaluacionMeses), 0), 1) * CONTENT_W : undefined;

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-hce-surface p-4">
      <div className="overflow-x-auto rounded-md border border-border">
        <div className="relative" style={{ minWidth: LABEL_W + CONTENT_W }}>
          {/* Guías verticales de edad: sin esto, alinear un ítem de "Lenguaje" con uno de "Motor Grueso" en la misma edad exige leer las coordenadas a ojo contra el eje de arriba — la misma línea compartida entre las 4 franjas hace tangible el eje común. Mismo tono ya usado para rejillas de gráficos (`rejilla` en `graficos/tema.ts`), no un color nuevo. */}
          {TICKS_EJE.map((tk) => (
            <div
              key={tk.meses}
              className="pointer-events-none absolute top-6 bottom-0 w-0 border-l"
              style={{ left: LABEL_W + mesesAPosicionEje(tk.meses) * CONTENT_W, borderColor: 'color-mix(in srgb, var(--color-hce-ink) 8%, transparent)' }}
            />
          ))}
          {lineaEdadIzquierda !== undefined && (
            <div className="pointer-events-none absolute top-6 bottom-0 z-20 w-0 border-l-2 border-dashed border-hce-clay" style={{ left: lineaEdadIzquierda }} />
          )}
          <EjeEdad />
          {FRANJAS_DENVER.map((franja) => (
            <FilaFranja key={franja.clave} franja={franja.clave} etiqueta={franja.etiqueta} estado={estado} onChangeItem={actualizarItem} />
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-hce-muted">
        {RESULTADOS.map((r) => (
          <span key={r} className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: `var(${ESTILO_POR_RESULTADO[r].variable})` }} />
            {t(`formularios.f028.resultados.${r}`)}
          </span>
        ))}
        <span className="border-l border-border pl-5">{t('formularios.f028.leyendaR')}</span>
      </div>
    </div>
  );
}
